import { stringReplacement } from '@mechhive/helpers';
import { Image, ImageFormat, ImageLoading } from '@mechhive/react';
import type { PayloadLink as PayloadLinkType } from '@repo/api-payload';
import { RewarbleIconsId } from '@repo/icons';
import { Icon } from '@repo/ui';
import clsx from 'clsx';
import { useState } from 'react';
import { useLanguage } from '~/hooks/useLanguage';
import { useRootLoaderData } from '~/hooks/useRootLoaderData';
import { LANGUAGE_DISPLAY_NAME } from '~/locale';
import { ChangeLanguageModal } from '~/modals/ChangeLanguageModal';
import { useModalStore } from './modal/ModalStore';
import { LinkPayload } from './payload/PayloadLink';

type CollapsableMenuProps = {
  label: string;
  items: Array<{
    link: PayloadLinkType;
    label: string;
    target?: string;
    icon?: RewarbleIconsId
  }>
}

const CollapsableMenu = ( { items, label }: CollapsableMenuProps ) => {
  const [ isOpen, setIsOpen ] = useState<boolean>( false );

  return (
    <div className={ 'border-b-2 border-[#64548D]/30 md:border-b-0 md:pb-5' }>
      <h4
        className={ 'py-5 md:pt-0 text-[#ABB0C7] hover:text-white md:pointer-events-none cursor-pointer text-base font-semibold flex flex-row items-center justify-between font-onest' }
        onClick={ () => {
          setIsOpen( !isOpen );
        } }>
        { label }
        <Icon
          name={ 'chevron-down' }
          className={ 'icon-size-5 h-5 w-5 md:hidden' }
        />
      </h4>
      <ul className={ clsx( {
        'hidden md:flex md:flex-col md:gap-5': !isOpen
      } ) }>
        { items.map( ( { label, link, icon }, index ) => {
          return (
            <li
              key={ index }
              className={ 'flex group items-center' }>
              <LinkPayload
                payloadLink={ link }
                className={ 'flex flex-row items-center gap-2 text-[#ABB0C7] text-sm hover:text-white py-[15px] md:py-0' }
              >
                { icon && (
                  <Icon
                    name={ icon }
                    className={ 'w-3.5 h-3.5 text-sm' } />
                )}
                <p className={ 'font-onest' }>
                  { label }
                </p>
              </LinkPayload>
            </li>
          )
        } ) }
      </ul>
    </div>
  )
}

export const Footer = () => {
  const language = useLanguage();
  const rootLoaderData = useRootLoaderData();

  const changeLanguageModalStore = useModalStore();

  return (
    <>
      <div className={ 'pb-[50px] flex flex-col w-full' }>
        <div className={ 'md:flex md:flex-row md:justify-between md:border-b-2 md:border-[#64548D]/30' }>
          <div className={ 'flex flex-col gap-10 md:gap-20 border-b-2 md:border-0 border-[#64548D]/30 pb-5' }>
            <Image
              provider={ 'images' }
              src={ '/rewarble-logo.svg' }
              alt={ 'Rewarble Logo' }
              className={ 'opacity-80' }
              width={ 32 }
              formats={ [
                ImageFormat.WebP,
                ImageFormat.PNG
              ] }
              sizes={ [
                {
                  width: 32
                }
              ] }
              dprVariants={ [ 1, 2 ] }
            />
            <button
              className={ 'flex gap-2.5 items-center group' }
              onClick={ () => {
                changeLanguageModalStore.show()
              } }>
              <Image
                provider={ 'images' }
                src={ `/flags/${ language.toUpperCase() }.svg` }
                className={ 'h-auto w-6 object-contain border border-[#FFFFFF]/50 rounded-sm cursor-pointer' }
                sizes={ [ { width: 24 } ] }
                alt={ `${ language.toUpperCase() } flag` }
                loading={ ImageLoading.Lazy }
                dprVariants={ [ 2 ] }
              />
              <div className={ 'group-hover:text-white font-onest' }>{LANGUAGE_DISPLAY_NAME[ language ]}</div>
            </button>
          </div>
          <div className={ 'md:flex md:flex-row md:gap-[100px]' }>
            { rootLoaderData?.footer.sections.map( ( section ) => {

              return (
                <CollapsableMenu
                  key={ section.id }
                  items={ section.items.map( ( item ) => ( {
                    label: item.label,
                    link: item.link,
                    icon: item.icon
                  } ) ) }
                  label={ section.title }
                />
              )
            } )}
          </div>
        </div>
        <p className={ 'text-[#ABB0C780] text-sm pt-5 font-onest' }>
          { stringReplacement( rootLoaderData?.footer.copyright ?? '', {
            'year': new Date().getFullYear()
          } ) }
        </p>
        <p className={ 'text-[#ABB0C780] text-xs pt-2 font-onest' }>
          { rootLoaderData?.footer.copyrightDisclaimer }
        </p>
      </div>
      <ChangeLanguageModal
        store={ changeLanguageModalStore }
      />
    </>
  )
}
